import {
  ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis,
  Tooltip, Area, Line
} from 'recharts';
import { HistoryEntry } from '@/types/fitness';

interface EvolutionChartProps {
  data: HistoryEntry[];
}

export function EvolutionChart({ data }: EvolutionChartProps) {
  // Sort chronologically — oldest first (left), newest last (right)
  const sorted = [...data].sort((a, b) => {
    // Parse DD/MM/YYYY
    const parseDate = (s: string) => {
      const parts = s.split('/');
      if (parts.length === 3) return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime();
      return 0;
    };
    return parseDate(a.fecha) - parseDate(b.fecha);
  });

  // Label each entry as Sesión 1, 2, 3... with Baseline first
  const labeled = sorted.map((entry, i) => ({
    ...entry,
    label: i === 0
      ? `Inicio\n${entry.fecha}`
      : `Ses. ${i}\n${entry.fecha}`,
    shortLabel: i === 0 ? 'Inicio' : `Ses. ${i}`,
  }));

  return (
    <div className="bg-card p-6 md:p-10 border-4 border-secondary shadow-xl" id="evolution-chart">
      <div className="flex justify-between items-center mb-8">
        <h3 className="text-2xl font-black italic uppercase tracking-tighter">Evolución Corporal</h3>
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-foreground rounded-full"></div>
            <span className="text-[10px] font-bold uppercase">Peso</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-accent rounded-full"></div>
            <span className="text-[10px] font-bold uppercase">Grasa</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span className="text-[10px] font-bold uppercase">IMC</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
            <span className="text-[10px] font-bold uppercase">M. Libre</span>
          </div>
        </div>
      </div>

      <div className="h-[400px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={labeled}>
            <defs>
              <linearGradient id="colorPeso" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--foreground))" stopOpacity={0.12} />
                <stop offset="95%" stopColor="hsl(var(--foreground))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
            <XAxis
              dataKey="shortLabel"
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11, fontWeight: 700 }}
              dy={10}
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'hsl(var(--foreground))', fontSize: 12, fontWeight: 900 }}
              domain={['dataMin - 2', 'dataMax + 2']}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'hsl(var(--accent))', fontSize: 12, fontWeight: 900 }}
              domain={['dataMin - 2', 'dataMax + 2']}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                // Find entry by shortLabel
                const entry = labeled.find(e => e.shortLabel === label);
                return (
                  <div className="bg-card border-2 border-secondary p-3 rounded shadow-lg">
                    <p className="text-xs font-black uppercase mb-1 text-foreground">{label}</p>
                    {entry && <p className="text-[10px] text-muted-foreground mb-2">{entry.fecha}</p>}
                    {payload.map((p: any, i: number) => (
                      <p key={i} className="text-sm font-bold" style={{ color: p.color }}>
                        {p.name}: {p.value}
                      </p>
                    ))}
                  </div>
                );
              }}
            />
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="peso"
              name="Peso (kg)"
              stroke="hsl(var(--foreground))"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorPeso)"
              dot={{ r: 6, fill: 'hsl(var(--foreground))', stroke: 'hsl(var(--accent))', strokeWidth: 2 }}
              activeDot={{ r: 8, fill: 'hsl(var(--foreground))', stroke: 'hsl(var(--accent))', strokeWidth: 3 }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="grasa"
              name="Grasa (%)"
              stroke="hsl(var(--accent))"
              strokeWidth={4}
              dot={{ r: 6, fill: 'hsl(var(--accent))', stroke: 'hsl(var(--foreground))', strokeWidth: 2 }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="imc"
              name="IMC"
              stroke="#3b82f6"
              strokeWidth={2}
              strokeDasharray="5 4"
              dot={{ r: 5, fill: '#3b82f6', stroke: '#1d4ed8', strokeWidth: 2 }}
              connectNulls
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="masaLibreGrasa"
              name="M. Libre Grasa (kg)"
              stroke="#10b981"
              strokeWidth={2}
              strokeDasharray="3 3"
              dot={{ r: 5, fill: '#10b981', stroke: '#059669', strokeWidth: 2 }}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* X axis dates below chart */}
      <div className="flex justify-between mt-2 px-10">
        {labeled.map((entry, i) => (
          <span key={i} className="text-[9px] text-muted-foreground font-bold text-center" style={{ flex: 1 }}>
            {entry.fecha}
          </span>
        ))}
      </div>
    </div>
  );
}
